import { trueFalseValues } from '@n2-common/uiFieldValues/applicationFormFields';
import { FormikValues } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CddAnswer } from '../../serverGeneratedTypings/cddAnswer';
import { Button, ButtonRowContainer, DismissableErrorMessage, Headline, InputGroup, Paragraph } from '../../ui';
import Wizard from '../MultiStageOnePageWizard';
import style from './style.module.scss';
import type { SubmitState, WizardStage } from '../MultiStageOnePageWizard/types';
import { SubmitProgressBox } from '@n2-common/ui/SubmitProgress';
import { Question } from '@n2-common/serverGeneratedTypings/question';
import { AnswerOption } from '@n2-common/serverGeneratedTypings/answerOption';
import { formikFieldsFromQuestions } from './formikFieldsFromQuestions';
import FileUploadButton from '../FileUploadButton';
import { DocumentInfo, Uploads } from '../UploadDocuments';
import { uploadOddDocument } from '@n2-common/services/oddService';
import { Box } from '@n2-common/myPagesUi/components/box/Box';
import { Spacing } from '@n2-common/myPagesUi/components/Layout/Spacing/Spacing';
import { logErrorToServerside } from '@n2-common/services/serverSideLogging';

export interface DependendQuestion {
    DependsOnFieldName: string;
    ShowIfValues: string[];
}

/*
const getFormikFieldsFilteredByQuestionDependencies = <TForm extends FormikValues>(
    formikFields: StageField<TForm>[],
    dependencies: Map<string, DependendQuestion>
) => {
    const fields: StageField<TForm>[] = [];
    formikFields.forEach((field) => {
        const dependendField = dependencies.get(field.name);
        field.hidden = field.hidden === undefined ? false : field.hidden;
        if (dependendField) {
            field.hidden = (values) => {
                const dependentValue = valueAt(values, dependendField.DependsOnFieldName);
                return !dependentValue || !dependendField.ShowIfValues.includes(dependentValue as string);
            };

            const validation = field.validation;// Capture original validation - we don't want stack overflow!
            field.validation = defineSchema(field.validation.type).when(dependendField.DependsOnFieldName, {
                This should be changed to test function (like in formikFieldsFromQuestions) if ever un-commented again :-)
                is: (val: string) => dependendField.ShowIfValues.includes(val),
                then: () => validation,
                otherwise: () => defineSchema(field.validation.type).nullable().notRequired(),
            });
        }
        fields.push(field);
    });
    return fields;
};*/

export interface PreFilledAnswer {
    questionCode: string;
    answerCode: string;
}

export interface CddQuestionGroups {
    amlPage1: string[];
    pepQuestions: string[];
    extendedQuestions: string[];
}
/*
export type CddSubmitState  = {
    submitState: SubmitState;
    askForExtendedInformation: boolean;
}*/

export type CddProps = {
    isPepFieldName: string;
    isPep: boolean;
    questions: Question[];
    documentUploadQuestions?: Question[];
    countries: AnswerOption[];
    questionGrups: CddQuestionGroups;
    preFilledAnswers?: PreFilledAnswer[] | null;
    hideHeadlines?: boolean;
    onSubmit: (
        payload: CddAnswer[],
        setSubmitState: (state: SubmitState) => void,
        setWizardStep: (step: CddSteps) => void,
        includesEnhanced: boolean
    ) => void;
};

export type CddFormProps = React.PropsWithChildren & CddProps;

export enum CddSteps {
    Aml,
    IsPep,
    Pep,
    Extended,
}

export default function CddForm({
    isPep,
    isPepFieldName,
    onSubmit,
    questionGrups,
    documentUploadQuestions,
    //  children,
    preFilledAnswers,
    questions,
    countries,
    hideHeadlines,
}: CddFormProps): JSX.Element {
    const [cddFormValues, setCddFormValues] = useState<FormikValues>({});
    const [pepFormValues, setPepFormValues] = useState<FormikValues>({});
    const [customerSaysPep, setCustomerSaysPep] = useState<boolean | null>(null);

    const { t } = useTranslation('common');

    const getSubmitButtonText = (submitState: SubmitState) => {
        switch (submitState) {
            case 'Done':
                return t<string>('FormSubmitButton.Done');
            case 'NotSent':
                return t<string>('FormSubmitButton.NextStep');
            case 'Processing':
                return t<string>('FormSubmitButton.Processing');
            case 'Submitted':
                return t<string>('FormSubmitButton.Submitted');
            case 'Error':
                return t<string>('FormSubmitButton.Error');
        }
        return t<string>('FormSubmitButton.NextStep');
    };
    const topOfFormRef = useRef<HTMLDivElement | null>(null);
    const [submitState, setSubmitState] = useState<SubmitState>('NotSent');
    const [wizardStep, setWizardStep] = useState<CddSteps>(CddSteps.Aml);
    const [showPepError, setShowPepError] = useState<boolean>(false);

    const [documentsPendingUpload, setdocumentsPendingUpload] = useState<boolean>(false);
    const emptyDocumentGuidList: CddAnswer[] =useMemo(() => {
         return documentUploadQuestions ? documentUploadQuestions.map((elm) => {return { QuestionCode: elm.QuestionCode, AnswerCode: null }}): [];
    }, [documentUploadQuestions]);
    const [uploadedDocumentGuids, setUploadedDocumentGuids ] = useState<CddAnswer[]>(emptyDocumentGuidList);

    useEffect(() => {
        if (documentUploadQuestions && documentUploadQuestions.length > 0) {
            setdocumentsPendingUpload(true);
        }
    }, [documentUploadQuestions]);

    const [cddGroups, pepGroups, extendedGroups] = useMemo(() => {
        const allQuestionsAsFormikFields = formikFieldsFromQuestions(questions, t, countries, preFilledAnswers);
        type TF = Parameters<Exclude<(typeof allQuestionsAsFormikFields)[number]['hidden'],boolean|undefined>>[0];
        const cddQuestionsAsFormikFields = allQuestionsAsFormikFields.filter((m) => questionGrups.amlPage1.includes(m.name));
        //const cddfields = getFormikFieldsFilteredByQuestionDependencies(cddQuestionsAsFormikFields, dependencies);
        const cddGroups: WizardStage<TF>[] = [{ fields: [...cddQuestionsAsFormikFields], legend: '', sectionNameForTracking: 'CDD' }];

        const pepQuestionsAsFormikFields = allQuestionsAsFormikFields.filter((m) => questionGrups.pepQuestions.includes(m.name));
        //    const pepFields = getFormikFieldsFilteredByQuestionDependencies(pepQuestionsAsFormikFields, dependencies);
        const pepGroups: WizardStage<TF>[] = [{ fields: pepQuestionsAsFormikFields, legend: '', sectionNameForTracking: 'PEP' }];

        const extendedQuestionsAsFormikFields = allQuestionsAsFormikFields.filter((m) => questionGrups.extendedQuestions.includes(m.name));
        //  const extendedFields = getFormikFieldsFilteredByQuestionDependencies(extendedQuestionsAsFormikFields, dependencies);
        const extendedGroups: WizardStage<TF>[] = [{ fields: extendedQuestionsAsFormikFields, legend: '', sectionNameForTracking: 'Extended' }];

        return [cddGroups, pepGroups, extendedGroups];
    }, [countries, preFilledAnswers, questionGrups, questions, t]);

    const onAmlSubmit = useCallback(
        (val: FormikValues) => {
            setCddFormValues(val);
            if (isPep) {
                setWizardStep(CddSteps.Pep);
            } else {
                setWizardStep(CddSteps.IsPep);
            }
        },
        [isPep]
    );

    useEffect(() => {
        if (!topOfFormRef) {
            return;
        }
        //console.log('Scrolling to top of form from step-change');
        topOfFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [topOfFormRef, wizardStep]);

    const onPepSubmitFunc = useCallback(
        (val: FormikValues) => {
            setPepFormValues(val);
            const total = getAnswerPayload(cddFormValues, val, [], questions, customerSaysPep, isPepFieldName);
            onSubmit(total, setSubmitState, setWizardStep, false);
        },
        [cddFormValues, customerSaysPep, isPepFieldName, onSubmit, questions]
    );

    const onExtendedSubmitFunc = useCallback(
        (val: FormikValues) => {
            const total = getAnswerPayload(cddFormValues, pepFormValues, val, questions, customerSaysPep, isPepFieldName);
            if (documentUploadQuestions) {
                documentUploadQuestions.forEach((e) => {
                    const documentGuidForDocumentQuestion = uploadedDocumentGuids.find(m => m.QuestionCode === e.QuestionCode);
                    if (!documentGuidForDocumentQuestion) {
                        logErrorToServerside("Unable to find document-guid for uploaded document when submitting e-odd. This should never happen", '', '', {QuestionCode: e.QuestionCode});
                        return;
                    }
                    total.push({
                        QuestionCode: e.QuestionCode,
                        AnswerCode: documentGuidForDocumentQuestion?.AnswerCode
                    })

                })
              
            }
            onSubmit(total, setSubmitState, setWizardStep, true);
        },
        [cddFormValues, customerSaysPep, isPepFieldName, onSubmit, questions, pepFormValues, documentUploadQuestions, uploadedDocumentGuids]
    );

    const onAreYouPepNextButtonFunc = useCallback(() => {
        if (customerSaysPep === null) {
            setShowPepError(true);
            return;
        }
        if (customerSaysPep) {
            setWizardStep(CddSteps.Pep);
        } else {
            const total = getAnswerPayload(cddFormValues, [], [], questions, customerSaysPep, isPepFieldName);
            onSubmit(total, setSubmitState, setWizardStep, false);
        }
    }, [cddFormValues, customerSaysPep, isPepFieldName, onSubmit, questions]);

    const cddInitialValues = useMemo(() => {
        const cddQuestionArr = questions.filter((m) => questionGrups.amlPage1.includes(m.QuestionCode));
        if (preFilledAnswers === null) {
            return null;
        }
        return generateInitialValuesFromFormAndPrefill(cddQuestionArr, preFilledAnswers);
    }, [preFilledAnswers, questionGrups, questions]);

    const pepInitialValues = useMemo(() => {
        if (preFilledAnswers === null) {
            return null;
        }
        const pepQuestionArr = questions.filter((m) => questionGrups.pepQuestions.includes(m.QuestionCode));
        return generateInitialValuesFromFormAndPrefill(pepQuestionArr, preFilledAnswers);
    }, [preFilledAnswers, questionGrups, questions]);

    const extendedInitialValues = useMemo(() => {
        if (preFilledAnswers === null) {
            return null;
        }
        const extendedQuestionArr = questions.filter((m) => questionGrups.extendedQuestions.includes(m.QuestionCode));
        return generateInitialValuesFromFormAndPrefill(extendedQuestionArr, preFilledAnswers);
    }, [preFilledAnswers, questionGrups, questions]);

    const headLines = useMemo(() => {
        if (wizardStep === CddSteps.Aml) {
            return (
                <>
                    <Headline>{t<string>('Cdd.AmlHeadline')}</Headline>
                    <Paragraph>{t<string>('Cdd.AmlDescription')}</Paragraph>
                </>
            );
        }
        if (wizardStep === CddSteps.Pep) {
            if (isPep) {
                return <Paragraph>{t<string>('Cdd.PepDescriptionIsPep')}</Paragraph>;
            }
            return <Paragraph>{t<string>('Cdd.PepDescriptionSelectedPep')}</Paragraph>;
        }
    }, [wizardStep, isPep, t]);

    return (
        <>
            {cddGroups &&
            cddGroups.length > 0 &&
            pepGroups &&
            pepGroups.length > 0 &&
            cddInitialValues &&
            pepInitialValues &&
            extendedInitialValues ? (
                <>
                    <SubmitProgressBox
                        state={submitState}
                        submitText={t<string>('Cdd.Submitted')}
                        processingText={t<string>('Cdd.Processing')}
                    ></SubmitProgressBox>
                    <BackButton wizardStep={wizardStep} setWizardStep={setWizardStep} isPep={isPep} />
                    <span ref={topOfFormRef}></span>
                    {hideHeadlines ? null : headLines}
                    {wizardStep === CddSteps.IsPep ? (
                        <div>
                            <Headline> {t<string>('Cdd.AreYouPepHeadline')}</Headline>
                            <InputGroup
                                value={customerSaysPep === null ? '' : customerSaysPep ? 'true' : 'false'}
                                name="IsPep"
                                onChange={(e) => {
                                    setCustomerSaysPep(e.target.value === 'true');
                                    setShowPepError(false);
                                }}
                                label={t<string>('Cdd.AreYouPepQuestion')}
                                type={'radio'}
                                options={trueFalseValues(t)}
                                helptext={t<string>('Cdd.WhatIsPep')}
                                error={showPepError && t<string>('Cdd.YouMustChoose')}
                                horisontal={true}
                            />

                            <ButtonRowContainer>
                                <Button type="button" onClick={() => onAreYouPepNextButtonFunc()}>
                                    {t<string>('FormSubmitButton.NextStep')}
                                </Button>
                            </ButtonRowContainer>
                        </div>
                    ) : null}
                    <div key="aml" className={wizardStep === CddSteps.Aml ? '' : style.wizardPartHidden}>
                        <Wizard
                            formName="cdd"
                            majorNumberUsedForTracking={wizardStep === CddSteps.Aml ? '3.1' : null}
                            initialValues={cddInitialValues}
                            stages={cddGroups}
                            getSubmitButtonText={getSubmitButtonText}
                            onSubmit={onAmlSubmit}
                        />
                    </div>
                    <div key="pep" className={wizardStep === CddSteps.Pep ? '' : style.wizardPartHidden}>
                        <Wizard
                            formName="pep"
                            majorNumberUsedForTracking={wizardStep === CddSteps.Pep ? '3.2' : null}
                            initialValues={pepInitialValues}
                            stages={pepGroups}
                            getSubmitButtonText={getSubmitButtonText}
                            onSubmit={onPepSubmitFunc}
                        />
                    </div>

                    <div key="extended" className={wizardStep === CddSteps.Extended ? '' : style.wizardPartHidden}>
                        {documentsPendingUpload && documentUploadQuestions ? (
                            <UploadDocumentsSection
                                documentUploadQuestions={documentUploadQuestions}
                                setallDocumentUploaded={() => setdocumentsPendingUpload(false)}
                                setUploadedDocumentGuids={setUploadedDocumentGuids}
                                uploadedDocumentGuids={uploadedDocumentGuids}
                            />
                        ) : (
                            <Wizard
                                formName="extended"
                                majorNumberUsedForTracking={wizardStep === CddSteps.Extended ? '3.3' : null}
                                initialValues={extendedInitialValues}
                                stages={extendedGroups}
                                getSubmitButtonText={getSubmitButtonText}
                                onSubmit={onExtendedSubmitFunc}
                            />
                        )}
                    </div>
                </>
            ) : (
                <></>
            )}
        </>
    );
}
function UploadDocumentsSection({
    documentUploadQuestions,
    setallDocumentUploaded,
    uploadedDocumentGuids,
    setUploadedDocumentGuids,

}: {
    documentUploadQuestions: Question[];
    setallDocumentUploaded: () => void;
    uploadedDocumentGuids: CddAnswer[];
    setUploadedDocumentGuids: (arr: CddAnswer[]) => void
}) {
    const { t } = useTranslation('mypage');

   /* const [documentsUploaded, setDocumentsUploaded] = useState<{ questionCode: string; uploaded: boolean }[]>(
        documentUploadQuestions.map((elm) => ({ questionCode: elm.QuestionCode, uploaded: false }))
    );*/

    const setDocumentUploaded = (questionCode: string, documentGuid: string) => {
        const newArr: CddAnswer[] = uploadedDocumentGuids.map((elm) => (elm.QuestionCode === questionCode ? { QuestionCode: elm.QuestionCode, AnswerCode: documentGuid } : elm));

        setUploadedDocumentGuids(newArr);
    };
    const allDocumentsUploaded = !uploadedDocumentGuids.find((m) => !m.AnswerCode);

    return (
        <>
            <Headline> {t<string>('dueDiligence.Documents.DocumentUploadsHeadline')}</Headline>
            <Spacing>
                {documentUploadQuestions.map((elm, i) => (
                    <UploadDocumentsSectionInner key={i} documentUploadQuestion={elm} setDocumentUploadedFunc={setDocumentUploaded} />
                ))}
            </Spacing>
            {allDocumentsUploaded && (
                <div>
                    <p>{t<string>('dueDiligence.Documents.AllDocumentUploaded')}</p>
                    <ButtonRowContainer>
                        <Button type="button" onClick={() => setallDocumentUploaded()}>
                            {t<string>('dueDiligence.Documents.DocumentUploadsNextStep')}
                        </Button>
                    </ButtonRowContainer>
                </div>
            )}
        </>
    );
}
function UploadDocumentsSectionInner({
    documentUploadQuestion,
    setDocumentUploadedFunc,
}: {
    documentUploadQuestion: Question;
    setDocumentUploadedFunc: (questionCode: string,  documentGuid: string) => void;
}) {
    const [working, setWorking] = useState(false);
    const { t } = useTranslation('mypage');

    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [documentUploaded, setDocumentUploaded] = useState<boolean>(false);

    const documentInfo: DocumentInfo[] = documentUploaded
        ? [{ status: '', deleteable: false, trusted: false, id: '', ref: '', filename: t<string>('dueDiligence.Documents.DocumentUploaded') }]
        : [];

    const uploadDocumentHandler = async (form: FormData) => {
        setWorking(true);
        setErrorMsg(null);
        try {
            const uploadRes = await uploadOddDocument(documentUploadQuestion.QuestionCode, form);
            setWorking(false);
            setDocumentUploaded(true);
            setDocumentUploadedFunc(documentUploadQuestion.QuestionCode, uploadRes.DocumentGuid);
        } catch (err) {
            setErrorMsg(t<string>('dueDiligence.Documents.UploadError'));
            setWorking(false);
        }
    };
    return (
        <Box type="whiteShadow">
            <p>
                {t<string>('dueDiligence.Documents.UploadDocumentHeadline')} {documentUploadQuestion.TranslationText}
            </p>
            <FileUploadButton
                uploadDocument={uploadDocumentHandler}
                disabled={documentUploaded}
                title={documentUploadQuestion.Description /*t<string>('Sign.Documents.UploadTitle')*/}
                buttonLabel={t<string>('dueDiligence.Documents.UploadButtonTitle')}
                uploadingLabel={t<string>('dueDiligence.Documents.UploadInProgress')}
            />

            <DismissableErrorMessage error={errorMsg} onDismiss={() => setErrorMsg(null)} />

            <Uploads
                documents={documentInfo}
                refreshing={working}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                deleteDocumentHandler={() => {}}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                footnoteSelector={() => {
                    return null;
                }}
            />
        </Box>
    );
}

function BackButton({
    wizardStep,
    setWizardStep,
    isPep,
}: {
    wizardStep: CddSteps;
    setWizardStep: (value: React.SetStateAction<CddSteps>) => void;
    isPep: boolean;
}) {
    if (wizardStep === CddSteps.Pep) {
        return <BackButtonElm wizardStep={isPep ? CddSteps.Aml : CddSteps.IsPep} setWizardStep={setWizardStep} />;
    }
    if (wizardStep === CddSteps.IsPep) {
        return <BackButtonElm wizardStep={CddSteps.Aml} setWizardStep={setWizardStep} />;
    }
    return null;
}

function BackButtonElm({ wizardStep, setWizardStep }: { wizardStep: CddSteps; setWizardStep: (value: React.SetStateAction<CddSteps>) => void }) {
    const { t } = useTranslation('common');

    return (
        <a
            className={style.backButton}
            href="#"
            onClick={(e) => {
                e.preventDefault();
                setWizardStep(wizardStep);
            }}
        >
            {'< '} {t<string>('Navigation.Back')}
        </a>
    );
}

function generateInitialValuesFromFormAndPrefill(questions: Question[], preFilledAnswers?: PreFilledAnswer[] | null) {
    if (!questions) {
        return null;
    }
    const initialValues: Record<string, string> = questions.reduce((m, q) => ({ ...m, [q.QuestionCode]: '' }), {});

    if (preFilledAnswers) {
        preFilledAnswers.forEach((q) => {
            const question = questions.find((m) => m.QuestionCode === q.questionCode);
            if (question) {
                //Only fill in prefilled if this is part of the questions
                initialValues[q.questionCode] = q.answerCode;
            }
        });
    }

    return initialValues;
}

function getAnswerPayload(
    amlValues: FormikValues,
    pepValues: FormikValues | null,
    enhancedValues: FormikValues | null,
    questions: Question[],
    customerSaidPep: boolean | null,
    isPepFieldName: string
) {
    const amlValuesArr = Object.entries(amlValues);
    const pepValuesArr = pepValues !== null ? Object.entries(pepValues) : null;
    const enhancedValuesArr = enhancedValues !== null ? Object.entries(enhancedValues) : null;
    const pepAndAmlValues = pepValuesArr !== null ? amlValuesArr.concat(pepValuesArr) : amlValuesArr;
    const allValues = enhancedValuesArr !== null ? pepAndAmlValues.concat(enhancedValuesArr) : pepAndAmlValues;

    const answerPayload = questions.map((q) => {
        const theAnswer = allValues.find((m) => m[0] === q.QuestionCode);
        let answerCode = theAnswer?.[1];
        if (customerSaidPep !== null && q.QuestionCode === isPepFieldName) {
            answerCode = customerSaidPep ? 'True' : 'False';
        }
        const retVal: CddAnswer = {
            QuestionCode: q.QuestionCode,
            AnswerCode: answerCode,
        };
        return retVal;
    });
    return answerPayload;
}
